// <pfs-wizard>: der interaktive, validierende Mehrschritt-Orchestrator (PFS-729).
// Die pfs-stepper.ts-Kommentare sahen ihn schon vor („der generische, FE-getriebene
// <pfs-wizard>-Container (next/back, Inhalts-Swap) … kommt mit der Terminbuchung").
//
// KOMPOSITION statt Monolith — der Wizard orchestriert die kleinen Primitive:
//   Zeilen        = <pfs-checklist-item> (Status-Dot + Label + gewählter Wert)
//   Validierung   = validation/validator (generisch, async, getextet)
//   pending       = <pfs-status-dot state=pending> (komponiert <pfs-loading-indicator>)
// Alle vier bleiben einzeln außerhalb des Wizards nutzbar.
//
// Verhalten: immer sichtbare Zusammenfassung (jeder Schritt eine Zeile mit Status
// + Wert), nur der aktive Schritt zeigt seinen Inhalt (Slot). Vorwärts ist per
// validateStep gegated (sync ODER async — dann pending), Zurück ist frei.
// Auto-Advance optional: commit() springt automatisch weiter.
//
// Properties (Kollektion/Controller → Property, PFS-662):
//   steps: WizardStep[]                              — Pflicht (Labels + summary())
//   validateStep?: (i:number) => Result | Promise    — Gate; fehlt = Vorwärts frei
//   autoAdvance?: boolean                            — commit() → next()
// data-* (Skalar): data-active (reflektiert), data-back-label, data-next-label,
//   data-finish-label.
// Inhalt: <div slot="step-0">…</div> je Schritt (nur der aktive wird gezeigt).
// Methoden: next() · prev() · goto(i) · commit()
// Events (Nicht-Form-Signal, PFS-663): pfs:step-change {from,to,direction} ·
//   pfs:step-finish (letztes „Weiter").

import { defineComponent } from "../../composition/define-component.js";
import { withShadow } from "../../composition/with-shadow.js";
import { SHADOW_RESET_CSS } from "../../styles/shadow-reset.js";
import { escapeHTML } from "../../utils/escape-html.js";
import { takeOwnProperty } from "../../utils/upgrade-property.js";
import { runValidator, type ValidationResult } from "../../validation/validator.js";

export interface WizardStep {
  label: string;
  // summary() = der immer sichtbare Wert der Zeile (gewählte Auswahl); leer = offen.
  summary?: () => string;
}

export type WizardValidateStep = (index: number) => ValidationResult | Promise<ValidationResult>;

export interface PfsWizardElement extends HTMLElement {
  steps: WizardStep[];
  validateStep?: WizardValidateStep;
  autoAdvance: boolean;
  next(): void;
  prev(): void;
  goto(index: number): void;
  commit(): void;
}

const STYLES = `
${SHADOW_RESET_CSS}
:host { display: block; }

.rows {
  border: 1px solid var(--pfs-color-outline);
  border-radius: var(--pfs-radius-md, 0.5rem);
  overflow: hidden;
  background: var(--pfs-color-surface);
}
.rows > pfs-checklist-item:not(:first-child) { border-top: 1px solid var(--pfs-color-outline); }

/* aktiver Inhalt sitzt eingerückt unter der aktiven Zeile (Dot-Spalte freilassen) */
.content { padding: 0 0.95rem 1rem 3.6rem; }

.valmsg {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.7rem;
  font-size: 0.83rem;
  font-weight: 500;
  color: var(--pfs-color-on-danger-container);
  background: var(--pfs-color-danger-container);
  border: 1px solid var(--pfs-color-danger);
  border-radius: var(--pfs-radius-sm, 0.375rem);
  padding: 0.55rem 0.8rem;
}
.valmsg svg { flex: none; }

.foot {
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
  margin-top: 1rem;
}
.foot .spacer { margin-left: auto; }

.btn {
  font: inherit;
  font-weight: 600;
  font-size: 0.9rem;
  padding: 0.55rem 1.1rem;
  border-radius: var(--pfs-radius-sm, 0.375rem);
  border: 1px solid transparent;
  cursor: pointer;
  transition: background-color 0.15s ease;
}
.btn:focus-visible { outline: none; box-shadow: var(--pfs-shadow-focus-ring-strong, 0 0 0 3px rgba(59,130,246,0.45)); }
.btn.next {
  background: var(--pfs-color-primary);
  border-color: var(--pfs-color-primary);
  color: var(--pfs-color-on-primary);
}
.btn.next:hover { background: var(--pfs-color-primary-hover); }
.btn.back {
  background: var(--pfs-color-surface);
  border-color: var(--pfs-color-outline-strong);
  color: var(--pfs-color-on-surface-muted);
}
.btn.back:hover { background: var(--pfs-color-surface-hover); }
.btn[disabled] { opacity: 0.55; cursor: not-allowed; }
.btn.hidden { visibility: hidden; }
`;

const WARN = `<svg viewBox="0 0 24 24" width="16" height="16" fill="none" aria-hidden="true"><path d="M10.3 4 2.4 18a2 2 0 0 0 1.7 3h15.8a2 2 0 0 0 1.7-3L13.7 4a2 2 0 0 0-3.4 0Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M12 9v4M12 17h.01" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

defineComponent({
  tagName: "pfs-wizard",
  mixins: [withShadow()],
  render(host, ctx) {
    const root = ctx.shadowRoot;
    if (!root) return;
    const self = host as PfsWizardElement;

    let steps: WizardStep[] = [];
    let validateStep: WizardValidateStep | undefined;
    let autoAdvance = false;
    let active = parseInt(host.dataset.active ?? "0", 10) || 0;
    let busy = false; // async-Validierung läuft
    let invalidAt = -1; // Schritt mit sichtbarem Fehler
    let message = ""; // Validierungstext
    let finished = false;

    const preSteps = takeOwnProperty<WizardStep[]>(host, "steps");
    const preValidate = takeOwnProperty<WizardValidateStep>(host, "validateStep");
    const preAuto = takeOwnProperty<boolean>(host, "autoAdvance");

    const style = document.createElement("style");
    style.textContent = STYLES;
    const rows = document.createElement("div");
    rows.className = "rows";
    const foot = document.createElement("div");
    foot.className = "foot";
    const back = document.createElement("button");
    back.type = "button";
    back.className = "btn back";
    const spacer = document.createElement("span");
    spacer.className = "spacer";
    const next = document.createElement("button");
    next.type = "button";
    next.className = "btn next";
    foot.append(back, spacer, next);
    root.append(style, rows, foot);

    const backLabel = (): string => host.dataset.backLabel ?? "Zurück";
    const nextLabel = (): string =>
      active >= steps.length - 1
        ? host.dataset.finishLabel ?? "Fertig"
        : host.dataset.nextLabel ?? "Weiter";

    const stateOf = (i: number): string => {
      if (i === active) return busy ? "pending" : "active";
      if (i === invalidAt) return "error";
      return i < active ? "done" : "open";
    };

    const paint = (): void => {
      // Zeilen (Zusammenfassung) — je Schritt ein <pfs-checklist-item>, dazwischen
      // der aktive Inhalt (Slot) direkt nach der aktiven Zeile.
      rows.replaceChildren();
      steps.forEach((step, i) => {
        const item = document.createElement("pfs-checklist-item");
        item.setAttribute("data-state", finished ? "done" : stateOf(i));
        item.setAttribute("data-symbol", String(i + 1));
        item.setAttribute("data-label", step.label);
        const val = step.summary?.() ?? "";
        if (val) item.setAttribute("data-value", val);
        // Vergangene/erreichbare Zeilen sind klickbar (Sprung); Navigation validiert.
        if (i !== active && !busy && !finished) {
          item.style.cursor = "pointer";
          item.addEventListener("click", () => self.goto(i));
        }
        rows.appendChild(item);

        if (i === active && !finished) {
          const content = document.createElement("div");
          content.className = "content";
          content.innerHTML = `<slot name="step-${i}"></slot>`;
          if (message) {
            const m = document.createElement("div");
            m.className = "valmsg";
            m.innerHTML = `${WARN}<span>${escapeHTML(message)}</span>`;
            content.appendChild(m);
          }
          rows.appendChild(content);
        }
      });

      back.textContent = backLabel();
      back.classList.toggle("hidden", active === 0 || finished);
      back.disabled = busy || finished;
      next.textContent = nextLabel();
      next.disabled = busy || finished;
    };

    const emit = (name: string, detail?: unknown): void => {
      host.dispatchEvent(new CustomEvent(name, { bubbles: true, composed: true, detail }));
    };

    const land = (target: number, direction: "forward" | "back"): void => {
      const from = active;
      active = target;
      host.dataset.active = String(active);
      invalidAt = -1;
      message = "";
      paint();
      emit("pfs:step-change", { from, to: target, direction });
    };

    const finish = (): void => {
      finished = true;
      invalidAt = -1;
      message = "";
      paint();
      emit("pfs:step-finish", { step: active });
    };

    const goto = async (target: number): Promise<void> => {
      if (busy || finished || target === active) return;
      const clamped = Math.max(0, Math.min(target, steps.length));
      if (clamped < active) {
        land(clamped, "back"); // Zurück/Rück-Sprung frei
        return;
      }
      // Vorwärts: alle durchlaufenen Schritte sequenziell validieren (async-fähig).
      busy = true;
      invalidAt = -1;
      message = "";
      paint();
      for (let i = active; i < clamped; i++) {
        const outcome = await runValidator(() => validateStep?.(i) ?? true);
        if (!outcome.ok) {
          busy = false;
          invalidAt = i;
          message = outcome.message ?? "";
          active = i;
          host.dataset.active = String(active);
          paint();
          return; // Stop beim ersten Fehler, Text anzeigen
        }
      }
      busy = false;
      if (clamped >= steps.length) finish();
      else land(clamped, "forward");
    };

    self.next = () => void goto(active + 1);
    self.prev = () => void goto(active - 1);
    self.goto = (i: number) => void goto(i);
    self.commit = () => {
      if (autoAdvance) void goto(active + 1);
      else paint(); // Auswahl registriert → Zusammenfassung aktualisieren
    };

    const onBack = (): void => self.prev();
    const onNext = (): void => self.next();
    back.addEventListener("click", onBack);
    next.addEventListener("click", onNext);
    ctx.cleanup.push(() => {
      back.removeEventListener("click", onBack);
      next.removeEventListener("click", onNext);
    });

    // Properties installieren (pre-connect-sicher).
    Object.defineProperty(host, "steps", {
      configurable: true,
      get: () => steps,
      set: (v: unknown) => {
        steps = Array.isArray(v) ? (v as WizardStep[]) : [];
        if (active > steps.length) active = Math.max(0, steps.length - 1);
        paint();
      },
    });
    Object.defineProperty(host, "validateStep", {
      configurable: true,
      get: () => validateStep,
      set: (v: unknown) => {
        validateStep = typeof v === "function" ? (v as WizardValidateStep) : undefined;
      },
    });
    Object.defineProperty(host, "autoAdvance", {
      configurable: true,
      get: () => autoAdvance,
      set: (v: unknown) => {
        autoAdvance = !!v;
      },
    });

    if (preValidate !== undefined) self.validateStep = preValidate;
    if (preAuto !== undefined) self.autoAdvance = preAuto;
    self.steps = preSteps ?? []; // löst den ersten paint() aus
  },
});
